import { Browser } from '@capacitor/browser';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import firebase from 'firebase/app';
import { FormEvent, useEffect, useReducer, useRef, useState } from 'react';
import { Button, Container, Form, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useFirebase } from '../../../contexts/FirebaseContext';
import { useCapacitor } from '../../../hooks/useCapacitor';
import { useQuery } from '../../../hooks/useQuery';
import { Copyable } from '../../components/Copyable/Copyable';
import { ErrorModal } from '../../components/ErrorModal/ErrorModal';
import { Footer } from '../../components/Footer/Footer';
import { FullscreenLoader } from '../../components/FullscreenLoader/FullscreenLoader';
import { Legalese } from '../../components/Legalese/Legalese';
import { Title } from '../../components/Title/Title';
import { Topbar } from '../../components/Topbar/Topbar';
import { formReducer, isValidPhoneNumber, verifyHashedQueryParams } from '../../utils/utils';
import styles from './FirstLogin.module.scss';
import { buildBrevoInterestAttributes } from './interest-key.helpers';

const FirstLoginComponent = () => {
  const { signupData, updateUserData, userDataFmt, addCheckoutSession, updateHubspotProperties, tags, database, currentUser, staticData, buyShop } =
    useFirebase();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  // const [billingData, setBillingData] = useState<PaymentMethodCreateParams.BillingDetails>({ address: {} });
  const [formData, setFormData] = useReducer(formReducer, {});
  const history = useHistory();
  const params = useQuery();
  const [loading, setLoading] = useState(false);
  const [tagsLoading, setTagsLoading] = useState(true);
  const { isNative } = useCapacitor();
  const [userTags, setUserTags] = useState<firebase.firestore.DocumentData[]>([]);
  const [missingTagImages, setMissingTagImages] = useState<Set<string>>(new Set());

  const [privacyDefault, setPrivacyDefault] = useState(false);
  const [privacyProfiling, setPrivacyProfiling] = useState(false);
  const [privacyMarketing, setPrivacyMarketing] = useState(false);
  const [privacySendNews, setPrivacySendNews] = useState(false);
  const [privacySendPhoneNews, setPrivacySendPhoneNews] = useState(false);
  const [privacySendCompanyData, setPrivacySendCompanyData] = useState(false);

  const [afterProfiling, setAfterProfiling] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const { t } = useTranslation();

  const [promoCodeReminderOpen, setPromoCodeReminderOpen] = useState(false);
  const skipFirstFalse = useRef(true);

  const stripe = useStripe();
  const elements = useElements();

  const proceedButtonText = t('firstLogin.labels.avanti');
  const [phoneError, setPhoneError] = useState(false);
  const phoneInputRef = useRef(null);

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (currentStep === 1) {
      if (phoneError) {
        if (phoneInputRef.current) {
          phoneInputRef.current.focus();
        }
        return;
      }
      setCurrentStep(2);
      return;
    }

    if (currentStep === 2) {
      setCurrentStep(3);
    }
  };

  useEffect(() => {
    if (!afterProfiling) return;

    (async () => {
      setLoading(true);

      const nome = (isNative ? currentUser.displayName || currentUser.email?.split('@')[0] : formData.nome) || '';
      const cognome = (isNative ? currentUser.displayName || currentUser.email?.split('@')[1] : formData.cognome) || '';

      const userData: any = {
        profile: {
          formData: { ...formData },
          tags: userTags.filter((tag) => tag.active).map((tag) => database.doc(`tags/${tag.id}`)),
          privacy: { privacyDefault, privacyProfiling, privacyMarketing, privacySendNews, privacySendPhoneNews, privacySendCompanyData } /*, paymentMethod */,
        },
      };

      userData.profile.formData.nome = nome;
      userData.profile.formData.cognome = cognome;

      if (formData.categoriaProfessionale == 'aso') userData.customTrialSeconds = 1800;
      userData.isNewTrial = !!staticData.ENABLE_NEW_TRIAL_FOR_NEW_USERS;
      userData.customTrialVideos = userData.isNewTrial ? 0 : staticData.DEFAULT_TRIAL_VIDEOS;
      await updateUserData(userData);

      const brevoTags = buildBrevoInterestAttributes({
        tags: userTags as any,
        selectedTagIds: userTags.filter((tag) => tag.active).map((tag) => tag.id),
      });

      await updateHubspotProperties({
        NOME: nome,
        COGNOME: cognome,
        SMS: userDataFmt?.profile?.formData?.phoneNumber || formData.phoneNumber,
        CATEGORIA_PROFESSIONALE: userDataFmt?.profile?.formData?.categoriaProfessionale || formData.categoriaProfessionale,
        ACCETTAZIONE_PRIVACY: privacyDefault,
        CONSENT_PROFILING: privacyProfiling,
        CONSENT_MARKETING: privacyMarketing,
        CONSENT_NEWS: privacySendNews,
        CONSENT_PHONE_NEWS: privacySendPhoneNews,
        CONSENT_COMPANY_DATA: privacySendCompanyData,
        ...brevoTags,
      });

      const shouldFfToCheckout = (await verifyHashedQueryParams(params, ['price'])) || params.comesFromPublicShop;

      if (shouldFfToCheckout) {
        if (params.code) setPromoCodeReminderOpen(true);
        else await createAndGoToCheckout();
      } else {
        // uso window.location.href invece di history per forzare il caricamento
        // del tempo della trial (nel caso uno sia aso)
        window.location.href = '/browse';
      }
    })();
  }, [afterProfiling]);

  useEffect(() => {
    if (promoCodeReminderOpen) return;
    if (skipFirstFalse.current) {
      skipFirstFalse.current = false;
      return;
    }
    createAndGoToCheckout().catch(console.error);
  }, [promoCodeReminderOpen]);

  const createAndGoToCheckout = async () => {
    try {
      /* Check if we are coming from:
       * 1. An ECM new subscription (params.ecm)
       * 2. A shop purchase (params.comesFromPublicShop)
       * 3. A regular purchase (params.price)
       */
      if (!!params.comesFromPublicShop) {
        // Go straight to the shop checkout page
        history.replace(
          `/shop?comesFromPublicShop=${params.comesFromPublicShop}&selectedProductSlug=${params.selectedProductSlug as any}&selectedProductId=${
            params.selectedProductId as any
          }&selectedProductPriceId=${params.selectedProductPriceId as any}`
        );
      } else if (!!params.ecm) {
        const { sessionId, url } = await addCheckoutSession(params.price, null, true);
        if (isNative) {
          await Browser.open({ url });
        } else await stripe.redirectToCheckout({ sessionId });
      } else {
        const { sessionId, url } = await addCheckoutSession(params.price);
        if (isNative) {
          await Browser.open({ url });
        } else await stripe.redirectToCheckout({ sessionId });
      }
    } catch (e) {
      try {
        alert(JSON.stringify(e, null, 2));
      } catch {
        alert(e);
      }
    }
  };

  const handleChange = (event) => {
    if (event.target.name == 'phoneNumber') {
      const isValid = event.target.value ? isValidPhoneNumber(event.target.value) : true;
      setPhoneError(!isValid);
    }

    setFormData({
      name: event.target.name,
      value: event.target.value,
    });
  };

  useEffect(() => {
    if (tags.length) {
      setTagsLoading(false);
      setUserTags(
        tags
          .filter((tag) => tag.interestKey || tag.hubspotTag)
          .filter((tag) => {
            const categoriaProfessionale = userDataFmt?.profile?.formData?.categoriaProfessionale || formData.categoriaProfessionale;
            return tag.visibleFor[categoriaProfessionale];
          })
          .map((tag) => ({ ...tag, active: false }))
      );
    }
  }, [tags, userDataFmt, formData]);

  useEffect(() => {
    if (userTags.some((tag) => tag.active)) {
      setPrivacyProfiling(true);
    }
  }, [userTags]);

  useEffect(() => {
    if (!privacyProfiling) setUserTags((current) => current.map((tag) => ({ ...tag, active: false })));
  }, [privacyProfiling]);

  const toggleTagActive = (id: string) => {
    setUserTags((current) => current.map((tag) => ({ ...tag, active: tag.id == id ? !tag.active : tag.active })));
  };

  const isTagActive = (id: string) => {
    return userTags.find((tag) => tag.id == id).active;
  };

  if (!userDataFmt || !currentUser || !signupData?.privacyChecks) return <FullscreenLoader />;
  // if (!shouldCompleteFirstLogin(userDataFmt)) return <Redirect to={'/'} />;

  return (
    <>
      <Title>{t('firstLogin.labels.titolo')}</Title>

      <Topbar showSubscribeButtonIfNeeded={false} showSearchBarIfNeeded={false} />

      <Container className={'my-5'}>
        <div className={`${styles.card} surface-card`}>
        <h1 className={'text-page-title text-center'}>{t('firstLogin.labels.benvenutoSu', { title: process.env.REACT_APP_TITLE })}</h1>
        <p className={'text-center mb-5'}>{t('firstLogin.labels.abbiamoBisognoInfo')}</p>

        <div className={styles.steps} aria-label={t('firstLogin.labels.benvenutoSu', { title: process.env.REACT_APP_TITLE })}>
          <div className={`${styles.step} ${currentStep === 1 ? styles.stepActive : ''} ${currentStep > 1 ? styles.stepComplete : ''}`}>
            <span className={styles.stepMarker}>{currentStep > 1 ? '\u2713' : '1'}</span>
            <span className={styles.stepLabel}>{t('firstLogin.labels.datiPersonali')}</span>
          </div>
          <span className={styles.stepConnector} />
          <div className={`${styles.step} ${currentStep === 2 ? styles.stepActive : ''} ${currentStep > 2 ? styles.stepComplete : ''}`}>
            <span className={styles.stepMarker}>{currentStep > 2 ? '\u2713' : '2'}</span>
            <span className={styles.stepLabel}>{t('firstLogin.labels.privacyStep')}</span>
          </div>
          <span className={styles.stepConnector} />
          <div className={`${styles.step} ${currentStep === 3 ? styles.stepActive : ''}`}>
            <span className={styles.stepMarker}>3</span>
            <span className={styles.stepLabel}>{t('firstLogin.labels.personalizzazioneEsperienza')}</span>
          </div>
        </div>

        <Form className="form-page" onSubmit={onSubmit} style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gridGap: '0.75rem' }}>
          {currentStep === 1 && <>
          {!isNative && (
            <Form.Group className={'mb-0'} style={{ gridArea: `span 1 / span 1` }}>
              <Form.Control name={'nome'} value={formData.nome || ''} disabled={loading} required={true} size={'lg'} placeholder={`${t('labels.nome')}*`} onChange={handleChange} />
            </Form.Group>
          )}
          {!isNative && (
            <Form.Group className={'mb-0'} style={{ gridArea: `span 1 / span 1` }}>
              <Form.Control name={'cognome'} value={formData.cognome || ''} disabled={loading} required={true} size={'lg'} placeholder={`${t('labels.cognome')}*`} onChange={handleChange} />
            </Form.Group>
          )}
          <Form.Group className={'mb-0'} style={{ gridArea: `span 1 / span 2` }}>
            <Form.Control
              ref={phoneInputRef}
              name={'phoneNumber'}
              value={formData.phoneNumber || ''}
              disabled={loading}
              required={true}
              size={'lg'}
              placeholder={`${t('profile.labels.phoneNumber')}*`}
              onChange={handleChange}
              onKeyPress={(event) => {
                const key = event.key;
                const regex = /^[0-9+]*$/;
                if (!regex.test(key)) {
                  event.preventDefault();
                }
              }}
              isInvalid={phoneError}
            />
            <Form.Control.Feedback type="invalid">{t('profile.labels.erroreFormatoTelefono')}</Form.Control.Feedback>
          </Form.Group>
          <Form.Group className={'mb-0'} style={{ gridArea: `span 1 / span 2` }}>
            <Form.Control name={'categoriaProfessionale'} value={formData.categoriaProfessionale || ''} required={true} size={'lg'} as={'select'} onChange={handleChange} disabled={loading}>
              <option value={''}>{t('labels.selezionaCategoriaProfessionale') + '*'}</option>
              <option value={'odontoiatra'}>{t('labels.odontoiatra')}</option>
              <option value={'aso'}>{t('labels.aso')}</option>
            </Form.Control>
          </Form.Group>

          <div className={`d-flex mt-4 ${styles.stepActions}`} style={{ gridArea: 'span 1 / span 2' }}>
            <Button className="tootor-button tootor-button--secondary" type={'submit'} disabled={loading}>
              {t('firstLogin.labels.avanti')}
            </Button>
          </div>
          </>}
          {/* <Form.Group>
            <Form.Label>Dati di fatturazione</Form.Label>
            <Form.Control required size={'lg'} placeholder={'Nome completo'} onChange={(e) => setBillingData((data) => ({ ...data, name: e.target.value }))} />
          </Form.Group>

          <Form.Group>
            <Form.Control
              required
              size={'lg'}
              placeholder={'Indirizzo'}
              onChange={(e) => setBillingData((data) => ({ ...data, address: { ...data.address, line1: e.target.value } }))}
            />
          </Form.Group>

          <Form.Row>
            <Form.Group as={Col}>
              <Form.Control
                required
                size={'lg'}
                placeholder={'Città'}
                onChange={(e) => setBillingData((data) => ({ ...data, address: { ...data.address, city: e.target.value } }))}
              />
            </Form.Group>
            <Form.Group as={Col}>
              <Form.Control
                required
                size={'lg'}
                placeholder={'Provincia'}
                onChange={(e) => setBillingData((data) => ({ ...data, address: { ...data.address, state: e.target.value } }))}
              />
            </Form.Group>
          </Form.Row>
          <Form.Row>
            <Form.Group as={Col}>
              <Form.Control
                required
                size={'lg'}
                placeholder={'CAP'}
                onChange={(e) => setBillingData((data) => ({ ...data, address: { ...data.address, postal_code: e.target.value } }))}
              />
            </Form.Group>
            <Form.Group as={Col}>
              <Form.Control
                required
                size={'lg'}
                placeholder={'Numero di telefono'}
                onChange={(e) => setBillingData((data) => ({ ...data, phone: e.target.value }))}
              />
            </Form.Group>
          </Form.Row> */}

          {/* <div className={'form-group'}>
            <div className={'form-control form-control-lg'} style={{ height: '2.4em', paddingTop: '0.6em' }}>
              <CardElement options={{ style: { base: { fontSize: '20px', fontFamily: 'Source Sans Pro, sans-serif' } } }} />
            </div>
          </div> */}

          {currentStep === 2 && <>
          <div style={{ gridArea: 'span 2 / span 2' }}>
            {t('firstLogin.labels.privacyDescription', { proceedButtonText })} <Legalese just={'tos'} /> {t('firstLogin.labels.privacyDescription2')}{' '}
            <Legalese just={'privacy'} />
            {t('firstLogin.labels.privacyDescription3')}
          </div>
          <Form.Group style={{ gridArea: 'span 1 / span 2' }}>
            <Form.Check
              type={'checkbox'}
              required={signupData.privacyChecks.checkPrivacyDefault}
              id={'checkPrivacyDefault'}
              disabled={loading}
              checked={privacyDefault}
              onChange={(e) => setPrivacyDefault(e.target.checked)}
              label={
                <>
                  <div>
                    {t('firstLogin.labels.privacyDescription4', { title: process.env.REACT_APP_TITLE })}
                    {!signupData.privacyChecks.checkPrivacyDefault && <small className={'text-italic'}> {t('firstLogin.labels.facoltativo')}</small>}
                  </div>
                </>
              }
            />
          </Form.Group>

          <Form.Group style={{ gridArea: 'span 1 / span 2' }}>
            <Form.Check
              type={'checkbox'}
              id={'checkPrivacyMarketing'}
              required={signupData.privacyChecks.checkPrivacyMarketing}
              disabled={loading}
              checked={privacyMarketing}
              onChange={(e) => setPrivacyMarketing(e.target.checked)}
              label={
                <>
                  <div>
                    {t('firstLogin.labels.acconsentoTrattamento', { letter: 'c)' })} {t('legalese.labels.privacy')},{' '}
                    {t('firstLogin.labels.acconsentoTrattamento3')}
                    {!signupData.privacyChecks.checkPrivacyMarketing && <small className={'text-italic'}> {t('firstLogin.labels.facoltativo')}</small>}
                  </div>
                </>
              }
            />
          </Form.Group>

          <Form.Group style={{ gridArea: 'span 1 / span 2' }}>
            <Form.Check
              type={'checkbox'}
              id={'checkPrivacySendNews'}
              required={signupData.privacyChecks.checkPrivacySendNews}
              disabled={loading}
              checked={privacySendNews}
              onChange={(e) => setPrivacySendNews(e.target.checked)}
              label={
                <>
                  <div>
                    {t('firstLogin.labels.acconsentoTrattamento', { letter: 'e)' })} {t('legalese.labels.privacy')},{' '}
                    {t('firstLogin.labels.acconsentoTrattamento2', { title: process.env.REACT_APP_TITLE })}
                    {!signupData.privacyChecks.checkPrivacySendNews && <small className={'text-italic'}> {t('firstLogin.labels.facoltativo')}</small>}
                  </div>
                </>
              }
            />
          </Form.Group>

          <Form.Group style={{ gridArea: 'span 1 / span 2' }}>
            <Form.Check
              type={'checkbox'}
              id={'checkPrivacySendPhoneNews'}
              required={signupData.privacyChecks.checkPrivacySendPhoneNews}
              disabled={loading}
              checked={privacySendPhoneNews}
              onChange={(e) => setPrivacySendPhoneNews(e.target.checked)}
              label={
                <>
                  <div>
                    {t('firstLogin.labels.acconsentoTrattamento', { letter: 'f)' })} {t('legalese.labels.privacy')},{' '}
                    {t('firstLogin.labels.acconsentoTrattamento4', { title: process.env.REACT_APP_TITLE })}
                    {!signupData.privacyChecks.checkPrivacySendPhoneNews && <small className={'text-italic'}> {t('firstLogin.labels.facoltativo')}</small>}
                  </div>
                </>
              }
            />
          </Form.Group>

          <Form.Group style={{ gridArea: 'span 1 / span 2' }}>
            <Form.Check
              type={'checkbox'}
              id={'checkPrivacySendCompanyData'}
              required={signupData.privacyChecks.checkPrivacySendCompanyData}
              disabled={loading}
              checked={privacySendCompanyData}
              onChange={(e) => setPrivacySendCompanyData(e.target.checked)}
              label={
                <>
                  <div>
                    {t('firstLogin.labels.acconsentoTrattamento', { letter: 'g)' })} {t('legalese.labels.privacy')},{' '}
                    {t('firstLogin.labels.acconsentoTrattamento5', { title: process.env.REACT_APP_TITLE })}
                    {!signupData.privacyChecks.checkPrivacySendCompanyData && <small className={'text-italic'}> {t('firstLogin.labels.facoltativo')}</small>}
                  </div>
                </>
              }
            />
          </Form.Group>

          <div className={`d-flex align-items-center mt-4 ${styles.stepActions}`} style={{ gridArea: 'span 1 / span 2', gap: '1rem' }}>
            <Button type={'button'} className="tootor-button tootor-button--basic" onClick={() => setCurrentStep(1)} disabled={loading}>
              {t('firstLogin.labels.indietro')}
            </Button>
            <Button className="tootor-button tootor-button--secondary" type={'submit'} disabled={loading}>
              {t('firstLogin.labels.avanti')}
            </Button>
          </div>
          </>}
          {currentStep === 3 && <div className={styles.profilingStep}>
            <div>{t('firstLogin.labels.qualiArgomentiInteressano')}</div>
            <p>{t('firstLogin.labels.accettaCondizioni', { letter: 'e)' })}</p>
            <div className={styles.profilingTagsContainerWrapper}>
              <div className={styles.profilingTagsContainer}>
                {tagsLoading && <FullscreenLoader fullscreen={false} />}
                {!tagsLoading && userTags.map((tag) => (
                  <div
                    key={tag.id}
                    className={`${styles.profilingTag} ${isTagActive(tag.id) ? styles.profilingTagActive : ''}`}
                    onClick={() => toggleTagActive(tag.id)}
                    data-label={tag.label}
                  >
                    {!!tag.image && !missingTagImages.has(tag.id) && (
                      <img
                        className={styles.profilingTagImage}
                        src={tag.image}
                        alt=""
                        onError={() => setMissingTagImages((current) => new Set(current).add(tag.id))}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
            <Form.Group className={'mt-2'}>
              <Form.Check
                type={'checkbox'}
                id={'checkPrivacyProfiling'}
                disabled={loading}
                checked={privacyProfiling}
                onChange={(e) => setPrivacyProfiling(e.target.checked)}
                label={<small>{t('firstLogin.labels.accettaCondizioni', { letter: 'c)' })} <Legalese just={'privacy'} />, {t('firstLogin.labels.acconsentoTrattamento3')}</small>}
              />
            </Form.Group>
            <div className={styles.stepActions}>
              <Button type={'button'} className="tootor-button tootor-button--basic" onClick={() => { setCurrentStep(2); setPrivacyProfiling(false); }}>
                {t('firstLogin.labels.indietro')}
              </Button>
              {!privacyProfiling && (
                <Button type={'button'} className="tootor-button tootor-button--secondary" onClick={() => setAfterProfiling(true)}>
                  {t('firstLogin.labels.procediSenzaPersonalizzazione')}
                </Button>
              )}
              {!!privacyProfiling && (
                <Button type={'button'} className="tootor-button tootor-button--secondary" disabled={!userTags.filter((tag) => tag.active).length} onClick={() => setAfterProfiling(true)}>
                  {t('labels.conferma')}
                </Button>
              )}
            </div>
          </div>}
        </Form>
        </div>
      </Container>

      <Footer />

      <ErrorModal isModalOpen={isModalOpen} setIsModalOpen={setIsModalOpen} errorMessage={errorMessage} />

      <Modal show={promoCodeReminderOpen} onHide={() => setPromoCodeReminderOpen(false)} backdrop={'static'} centered keyboard={false}>
        <Modal.Header>
          <Modal.Title>{t('firstLogin.labels.ricorcaCodiceTitle')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div>
            {t('firstLogin.labels.ricordaCodice')}{' '}
            <strong>
              <Copyable text={params?.code} />
            </strong>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant={'primary'} onClick={() => setPromoCodeReminderOpen(false)}>
            {t('firstLogin.labels.vaiPagamento')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default FirstLoginComponent;
